/**
 * Liens vers les sources et les services externes, sans dépendance serveur :
 * importable par les fronts.
 */

export function hostOf(url: string) {
  try {
    return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/**
 * Une adresse cliquable : l'agent rend parfois un domaine nu (« site.fr »),
 * qu'un navigateur prendrait pour un chemin relatif de la page.
 */
export function absoluteUrl(url: string) {
  const trimmed = url.trim();
  return /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed.replace(/^\/+/, "")}`;
}

/** Les caractères qui ont un sens dans la syntaxe `:~:text=` doivent être échappés en plus. */
function encodeFragmentText(text: string) {
  return encodeURIComponent(text).replace(/-/g, "%2D").replace(/,/g, "%2C").replace(/&/g, "%26");
}

const MAX_EXACT_QUOTE = 80;
const EDGE_WORDS = 5;

/**
 * L'URL de la source ouverte sur la phrase citée : Chrome, Edge et Safari font
 * défiler la page jusqu'au passage et le surlignent (fragment de texte). Une
 * longue citation passe en `début,fin`, plus tolérant aux écarts de mise en
 * page au milieu de la phrase. Les autres navigateurs ignorent le fragment.
 */
export function textFragmentUrl(url: string, quote: string | null | undefined) {
  const clean = quote?.replace(/\s+/g, " ").replace(/^[«"“\s]+|[»"”\s]+$/g, "").trim();
  if (!clean || clean.length < 12) return url;
  let directive: string;
  if (clean.length <= MAX_EXACT_QUOTE) {
    directive = encodeFragmentText(clean);
  } else {
    const words = clean.split(" ");
    if (words.length < EDGE_WORDS * 2 + 1) {
      directive = encodeFragmentText(clean);
    } else {
      directive = `${encodeFragmentText(words.slice(0, EDGE_WORDS).join(" "))},${encodeFragmentText(words.slice(-EDGE_WORDS).join(" "))}`;
    }
  }
  return `${url}${url.includes("#") ? "" : "#"}:~:text=${directive}`;
}

/** Brouillon Gmail déjà rempli (destinataire, objet, corps). */
export function gmailComposeUrl({ to, subject, body }: { to?: string | null; subject?: string | null; body: string }) {
  const params = new URLSearchParams({ view: "cm", fs: "1" });
  if (to) params.set("to", to);
  if (subject) params.set("su", subject);
  params.set("body", body);
  return `https://mail.google.com/mail/?${params.toString()}`;
}

/** La messagerie par défaut du poste (Outlook, Apple Mail…). */
export function mailtoUrl({ to, subject, body }: { to?: string | null; subject?: string | null; body: string }) {
  const query = [subject && `subject=${encodeURIComponent(subject)}`, `body=${encodeURIComponent(body)}`]
    .filter(Boolean)
    .join("&");
  return `mailto:${to ? encodeURIComponent(to) : ""}?${query}`;
}

/** Fiche légale (Pappers) et adresse (Google Maps) d'une organisation, par son nom. */
export function pappersSearchUrl(name: string) {
  return `https://www.pappers.fr/recherche?q=${encodeURIComponent(name)}`;
}

export function mapsSearchUrl(name: string) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`;
}

export function linkedinCompanySearchUrl(name: string) {
  return `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(name)}`;
}
